/**
 * Complimentary gift auto-add (Bedable).
 *
 * A <complimentary-gift> element is rendered by snippets/complimentary-gift.liquid
 * inside the cart drawer (#MiniCart-…) and the cart page (#MainCart-…), carrying
 * the current cart state as data attributes. Both containers re-render on every
 * cart update, so each render re-connects a fresh element and reconcile() runs
 * again — the cart converges without event wiring.
 *
 * Rules (config in theme settings, "Complimentary gift"):
 *  - eligible buyers with a qualifying subtotal get exactly ONE gift line at qty 1
 *  - below the threshold, ineligible, or in cleanup mode: every gift line removed
 *  - surplus gift lines (crafted permalinks, extra variants) are collapsed to one
 *
 * Convergence: reconcile() returns early when no change is needed, so it never
 * re-publishes on a no-op — the add/remove/clamp cases each change cart state,
 * which then reconciles to a stable no-op. A persistent server error stops the
 * loop after a few tries via the failure counter.
 */
(() => {
  if (customElements.get('complimentary-gift')) return;

  let inFlight = false;
  let halted = false;   // stop retrying for this page view (broken product / repeated errors)
  let failures = 0;

  customElements.define(
    'complimentary-gift',
    class ComplimentaryGift extends HTMLElement {
      connectedCallback() {
        // Wait a frame so we never mutate the cart mid-render.
        requestAnimationFrame(() => this.reconcile());
      }

      get variantId() {
        return parseInt(this.getAttribute('data-variant-id')) || 0;
      }

      get giftKeys() {
        const raw = this.getAttribute('data-gift-keys') || '';
        return raw ? raw.split(',') : [];
      }

      get giftQty() {
        return parseInt(this.getAttribute('data-gift-qty')) || 0;
      }

      get eligible() {
        return this.hasAttribute('data-eligible');
      }

      get qualifies() {
        const threshold = parseInt(this.getAttribute('data-threshold')) || 0;
        const subtotal = parseInt(this.getAttribute('data-subtotal')) || 0;
        return subtotal > 0 && subtotal >= threshold;
      }

      reconcile() {
        if (inFlight || halted || !this.isConnected) return;

        const wanted = this.eligible && this.qualifies ? 1 : 0;
        const keys = this.giftKeys;

        // No gift in cart yet: add one if wanted (and we have a real variant).
        if (keys.length === 0) {
          if (wanted === 1 && this.variantId > 0) this.addGift();
          return;
        }

        // Gift line(s) present: keep exactly one at `wanted`, zero any extras.
        // Skip when already in the target state (one line, qty 1, wanted 1).
        const needChange = wanted === 0 || this.giftQty !== 1 || keys.length > 1;
        if (!needChange) return;

        const updates = {};
        keys.forEach((key, i) => { updates[key] = i === 0 ? wanted : 0; });
        this.applyUpdates(updates);
      }

      bundledSections() {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } }));
        return sections;
      }

      addGift() {
        // Add via /cart/update.js keyed by variant id, NOT /cart/add.js:
        // Shopify's web pixels report every cart/add as product_added_to_cart,
        // so the auto-added £0 gift was feeding fake add_to_cart events to
        // GA4/Meta/Google ad optimisation. Cart updates only emit cart_updated.
        // /cart/update.js adds the variant when it is not in the cart yet.
        this.applyUpdates({ [this.variantId]: 1 });
      }

      async applyUpdates(updates) {
        inFlight = true;
        try {
          const body = JSON.stringify({
            updates,
            sections: this.bundledSections()
          });

          const response = await fetch(theme.routes.cart_update_url, { ...theme.utils.fetchConfig(), body });
          const parsed = await response.json();

          if (!response.ok || parsed.errors) {
            if (++failures >= 3) halted = true;
            return;
          }

          failures = 0;
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'complimentary-gift', cart: parsed });
        }
        catch (error) {
          if (++failures >= 3) halted = true;
          console.error('[complimentary-gift]', error);
        }
        finally {
          inFlight = false;
        }
      }
    }
  );
})();
